/**
 * Pure comparison for roster replacement. `setAgents` (store.ts) needs to
 * know whether a newly validated roster actually differs from what's on
 * screen, so a same-content poll never triggers a React re-render or makes
 * AgentSpace reallocate its 3D field's materials/geometries (it memoizes
 * those on the agent id-set — see its `structKey`).
 *
 * Pure module: no Next/React/store imports — importable directly under
 * `node --test --experimental-strip-types`.
 */

import type { LabAgent } from './types.ts';

/** FNV-1a fold over one string, continuing from `h`. */
function hashPart(h: number, s: string): number {
  let out = h;
  for (let i = 0; i < s.length; i++) {
    out ^= s.charCodeAt(i);
    out = Math.imul(out, 16777619);
  }
  return out >>> 0;
}

/**
 * A cheap, order-independent fingerprint of a roster: the sorted id list
 * (so membership changes are always caught, even at equal length) folded
 * together with every field that can change an agent's on-screen presence.
 */
export function rosterSignature(agents: readonly LabAgent[]): string {
  const sorted = [...agents].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  let h = 2166136261;
  for (const a of sorted) {
    const connections = [...a.connections].sort().join(',');
    const part = [
      a.id,
      a.name,
      a.kind,
      a.role,
      a.task,
      a.attachedTo ?? '',
      a.status,
      a.activity.toFixed(3),
      a.cognition ?? '',
      a.tier ?? '',
      a.model ?? '',
      a.generation ?? '',
      a.retiredTick ?? '',
      connections,
    ].join('');
    h = hashPart(h, part);
    h = hashPart(h, ''); // record separator, so "ab|c" and "a|bc" fold apart
  }
  return `${sorted.length}:${sorted.map((a) => a.id).join(',')}:${h.toString(16)}`;
}

/** True when two rosters would render identically (same members, same fields). */
export function rostersEqual(a: readonly LabAgent[], b: readonly LabAgent[]): boolean {
  if (a === b) return true;
  return rosterSignature(a) === rosterSignature(b);
}
